import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  MessageSquare,
  Database,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Building2,
  Globe,
  X,
  Palette,
  UserCircle,
  Zap,
  Megaphone,
  CreditCard,
  Rocket,
  MessageSquareCode,
  MessageCircle,
  ArrowLeft,
  Activity,
  Shield,
  ScrollText,
  Wrench,
  ChevronDown,
  ChevronRight,
  FileText,
  Code,
  Brain,
} from 'lucide-react';
import { hasPermission } from '@lasoxp/domain-logic';
import { useAuth } from '../../contexts/AuthContext';
import { useProperty } from '../../contexts/PropertyContext';
import { useQueueCounts } from '../../hooks/useQueueCounts';
import type { Permission } from '../../types/permissions';
import type { Property } from '../../types';
import logoFull from '../../assets/logo_full.png';
import { devError } from '../../utils/dev-log';
import { Button } from '../shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NavItemContext {
  /** The currently selected property, if any */
  property: Property | null;
}

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  /** Permission required to see this item */
  requiredPermission?: Permission;
  /** Role required to see this item (primaryRole match). Combined with OR against requiredSuper. */
  requiredRole?: string;
  /** Only visible to super admins */
  requiredSuper?: boolean;
  /**
   * Optional predicate that hides the item when it returns true.
   * Used e.g. to auto-hide the "Getting Started" checklist after launch.
   * The route itself stays mounted in App.tsx so deep links still work.
   */
  hiddenWhen?: (ctx: NavItemContext) => boolean;
}

// ---------------------------------------------------------------------------
// Launch state detection
// ---------------------------------------------------------------------------

/**
 * A property is considered "launched" once the Go Live onboarding step has
 * flipped it to `active`. The backend stamps `launchedAt` on that transition
 * (see `routes/admin/settings.ts`), so the preferred signal is simply the
 * presence of that field.
 *
 * Legacy fallback: properties created before `launchedAt` existed do not yet
 * have the field stamped. For those we fall back to the old heuristic
 * (`status === 'active'` AND widget configuration saved), which mirrors the
 * backend onboarding checklist's `hasWidgetConfig && isActive` gate. Once the
 * backfill script (`scripts/backfill-property-launched-at.ts`) has run across
 * all live properties, the fallback branch can be deleted.
 */
function isPropertyLaunched(property: Property | null): boolean {
  if (!property) return false;
  // Preferred: explicit launchedAt stamped by Go Live.
  if (property.launchedAt) return true;
  // Legacy fallback for properties that pre-date the launchedAt field.
  if (property.status !== 'active') return false;
  const widget = property.widgetConfig;
  return !!(widget && (widget.primaryColor || widget.theme));
}

interface NavSection {
  /** Label shown as a small-caps divider */
  label?: string;
  items: NavItem[];
}

// ---------------------------------------------------------------------------
// Super Admin menu (no property selected)
// ---------------------------------------------------------------------------

const adminMainSections: NavSection[] = [
  {
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      { name: 'Organizations', href: '/organizations', icon: Building2 },
      { name: 'Properties', href: '/properties', icon: Globe },
      { name: 'Billing & Plans', href: '/billing', icon: CreditCard, requiredPermission: 'system:full_access' },
    ],
  },
];

const adminAdvancedItems: NavItem[] = [
  { name: 'Audit Log', href: '/audit', icon: ScrollText, requiredPermission: 'system:full_access' },
  { name: 'Platform Health', href: '/platform-health', icon: Activity, requiredSuper: true },
  { name: 'AI Ops', href: '/ai-ops', icon: Brain, requiredSuper: true },
  { name: 'Sales Provisioning', href: '/sales-provisioning', icon: Wrench, requiredSuper: true },
  { name: 'Internal Access', href: '/internal-access', icon: Shield, requiredSuper: true },
  { name: 'Brands', href: '/brands', icon: Palette, requiredPermission: 'brand:read' },
  { name: 'Design System', href: '/design-system', icon: Palette, requiredSuper: true },
];

// ---------------------------------------------------------------------------
// Property mode menu — organized around the four product jobs
// ---------------------------------------------------------------------------

function getPropertySections(propertyId: string): NavSection[] {
  const p = (path: string) => `/properties/${propertyId}/${path}`;
  return [
    {
      label: 'Setup',
      items: [
        {
          name: 'Getting Started',
          href: p('setup'),
          icon: Rocket,
          requiredPermission: 'property:read',
          // Hide from the sidebar once the property has been fully launched.
          // The route stays mounted so deep links to /properties/:id/setup
          // still resolve for anyone who wants to revisit the checklist.
          hiddenWhen: ({ property }) => isPropertyLaunched(property),
        },
        { name: 'Property Settings', href: p('settings'), icon: Settings, requiredPermission: 'property:update' },
        { name: 'Knowledge Base', href: p('knowledge-base'), icon: Database, requiredPermission: 'kb:read' },
        // Canonical widget install surface. Legacy `#embed` bookmarks still
        // resolve on the Property Settings page, but the sidebar now points at
        // the dedicated route.
        { name: 'Widget Install', href: p('widget-install'), icon: Code, requiredPermission: 'property:update' },
      ],
    },
    {
      label: 'Live',
      items: [
        { name: 'Dashboard', href: `/properties/${propertyId}`, icon: LayoutDashboard },
        { name: 'Live Sessions', href: p('sessions'), icon: MessageSquare, requiredPermission: 'chat:read' },
        { name: 'Handover Queue', href: p('handovers'), icon: Users, requiredPermission: 'handover:read' },
        { name: 'Guest Profiles', href: p('guests'), icon: UserCircle },
      ],
    },
    {
      label: 'Engage',
      items: [
        { name: 'Automation', href: p('automation'), icon: Zap, requiredPermission: 'notifications:read' },
        { name: 'Templates', href: `${p('automation')}?tab=templates`, icon: FileText, requiredPermission: 'notifications:read' },
        { name: 'Campaigns', href: p('campaigns'), icon: Megaphone, requiredPermission: 'campaigns:read' },
        { name: 'Test Chat', href: p('test-chat'), icon: MessageSquareCode, requiredPermission: 'chat:read' },
      ],
    },
    {
      label: 'Performance',
      items: [
        { name: 'Analytics', href: p('analytics'), icon: BarChart3, requiredPermission: 'analytics:read' },
        { name: 'Feedback', href: p('feedback'), icon: MessageCircle, requiredPermission: 'property:read' },
      ],
    },
  ];
}

function getPropertyAccountSection(propertyId: string): NavSection {
  const p = (path: string) => `/properties/${propertyId}/${path}`;
  return {
    label: 'Account',
    items: [
      { name: 'Team', href: p('team'), icon: Users, requiredPermission: 'users:read' },
      { name: 'Billing', href: p('billing'), icon: CreditCard, requiredPermission: 'billing:read' },
      { name: 'Security', href: '/account/security', icon: Shield },
    ],
  };
}

function getPropertyAdvancedItems(propertyId: string): NavItem[] {
  const p = (path: string) => `/properties/${propertyId}/${path}`;
  return [
    // Property-level audit log (visible to anyone with property:read)
    { name: 'Audit Log', href: p('audit'), icon: ScrollText, requiredPermission: 'property:read' },
    // Org-level surfaces — visible to org_admin and super admin
    { name: 'Brands', href: '/brands', icon: Palette, requiredPermission: 'brand:read' },
    // Super-admin-only platform surfaces
    { name: 'Platform Health', href: '/platform-health', icon: Activity, requiredSuper: true },
    { name: 'AI Ops', href: '/ai-ops', icon: Brain, requiredSuper: true },
    { name: 'Sales Provisioning', href: '/sales-provisioning', icon: Wrench, requiredSuper: true },
    { name: 'Internal Access', href: '/internal-access', icon: Shield, requiredSuper: true },
    { name: 'Design System', href: '/design-system', icon: Palette, requiredSuper: true },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SidebarProps {
  mobile?: boolean;
  onClose?: () => void;
}

const ADVANCED_OPEN_KEY = 'lasoxp-admin-sidebar-advanced-open';

export function Sidebar({ mobile, onClose }: SidebarProps) {
  const { signOut, adminUser } = useAuth();
  const { selectedProperty, setSelectedProperty, isSuperAdmin } = useProperty();
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionCount, handoverCount } = useQueueCounts(selectedProperty?.id);

  // Advanced drawer state — persisted to localStorage so the user's preference
  // survives reloads and property switches.
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ADVANCED_OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(ADVANCED_OPEN_KEY, advancedOpen ? '1' : '0');
    } catch {
      // Ignore storage errors (Safari private mode, quota, etc.)
    }
  }, [advancedOpen]);

  /** Extract the `tab` query param from a search string (e.g. "?tab=templates"). */
  const tabFromSearch = (search: string): string | null => {
    if (!search) return null;
    const normalized = search.startsWith('?') ? search.slice(1) : search;
    const params = new URLSearchParams(normalized);
    return params.get('tab');
  };

  /**
   * Parse a nav item href into its components: pathname, tab query, hash.
   * Handles the four cases: plain path, path+query, path+hash, path+query+hash.
   * Fragment is parsed before query because hashes can legally contain "?".
   */
  const parseHref = (href: string): { pathname: string; tab: string | null; hash: string | null } => {
    // Strip fragment first (fragment can technically contain "?", so do hash before query)
    const hashIdx = href.indexOf('#');
    const withoutHash = hashIdx === -1 ? href : href.slice(0, hashIdx);
    const hash = hashIdx === -1 ? null : href.slice(hashIdx + 1) || null;
    const [pathname, search = ''] = withoutHash.split('?');
    return { pathname, tab: tabFromSearch(search), hash };
  };

  /**
   * Determine whether a nav item should render as active.
   *
   * Collision fix: two items can share the same pathname and differ only by
   * `?tab=...` or `#hash`. Without disambiguation, both light up together.
   *
   * Examples:
   * - Automation vs Templates: same path, differ by `?tab=templates`.
   * - Property Settings vs legacy embed anchor: same path, differ by `#embed`.
   *
   * Rules:
   *  1. If the item's href has a `?tab=...`, it only matches when the current
   *     URL has the same tab query.
   *  2. If the item's href has a `#hash`, it only matches when the current URL
   *     has the same hash.
   *  3. If the item's href has neither tab nor hash but a sibling in the same
   *     section claims the current tab or hash, yield to that sibling.
   *  4. Otherwise fall back to the startsWith pathname match.
   *
   * Expected matrix for property settings/install variants:
   * - `/properties/X/settings` (no hash)      → Property Settings active
   * - `/properties/X/settings#embed`          → Property Settings active and the page can scroll to Embed Code
   * - `/properties/X/widget-install`          → Widget Install active
   */
  const isActive = (item: NavItem, siblings: NavItem[] = []) => {
    const { pathname, tab: itemTab, hash: itemHash } = parseHref(item.href);
    const currentTab = tabFromSearch(location.search);
    const currentHash = location.hash ? location.hash.replace(/^#/, '') : null;

    // Exact match for root-ish paths
    if (pathname === '/' || pathname === `/properties/${selectedProperty?.id}`) {
      return location.pathname === pathname;
    }

    // Tabbed item only matches when pathname AND tab both match.
    if (itemTab) {
      return location.pathname === pathname && currentTab === itemTab;
    }

    // Hashed item only matches when pathname AND hash both match.
    if (itemHash) {
      return location.pathname === pathname && currentHash === itemHash;
    }

    // Plain item: if a sibling with the same pathname owns the current tab or
    // hash, yield to that sibling so it wins the highlight.
    if (currentTab || currentHash) {
      const siblingOwnsDiscriminator = siblings.some((sib) => {
        if (sib === item) return false;
        const sibParsed = parseHref(sib.href);
        if (sibParsed.pathname !== pathname) return false;
        if (currentTab && sibParsed.tab === currentTab) return true;
        if (currentHash && sibParsed.hash === currentHash) return true;
        return false;
      });
      if (siblingOwnsDiscriminator) return false;
    }

    return location.pathname.startsWith(pathname);
  };

  const navContext = useMemo<NavItemContext>(
    () => ({ property: selectedProperty }),
    [selectedProperty],
  );

  const canSee = (item: NavItem): boolean => {
    if (item.requiredSuper && !isSuperAdmin) return false;
    if (item.requiredRole && adminUser?.primaryRole !== item.requiredRole && !isSuperAdmin) {
      return false;
    }
    if (item.hiddenWhen && item.hiddenWhen(navContext)) return false;
    if (item.requiredPermission) {
      return hasPermission(adminUser?.permissions ?? [], item.requiredPermission);
    }
    return true;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      devError('Sign out failed:', error);
    }
  };

  const handleNavClick = () => {
    if (mobile && onClose) onClose();
  };

  const isPropertyMode = !!selectedProperty;
  const showAdminMode = isSuperAdmin && !isPropertyMode;

  const propertySections = useMemo(
    () => (selectedProperty ? getPropertySections(selectedProperty.id) : []),
    [selectedProperty],
  );
  const propertyAccountSection = useMemo(
    () => (selectedProperty ? getPropertyAccountSection(selectedProperty.id) : null),
    [selectedProperty],
  );
  const propertyAdvancedItems = useMemo(
    () => (selectedProperty ? getPropertyAdvancedItems(selectedProperty.id) : []),
    [selectedProperty],
  );

  const getBadge = (itemName: string): { count: number; color: string } | null => {
    if (itemName === 'Handover Queue' && handoverCount > 0) {
      return { count: handoverCount, color: 'bg-danger-500' };
    }
    if (itemName === 'Live Sessions' && sessionCount > 0) {
      return { count: sessionCount, color: 'bg-info-500' };
    }
    return null;
  };

  const renderNavItem = (item: NavItem, siblings: NavItem[] = []) => {
    const active = isActive(item, siblings);
    const badge = getBadge(item.name);
    return (
      <NavLink
        key={`${item.name}-${item.href}`}
        to={item.href}
        onClick={handleNavClick}
        className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
          active
            ? 'bg-primary-50 text-primary-700 font-semibold'
            : 'text-surface-600 hover:bg-surface-100 hover:text-surface-800'
        }`}
      >
        <item.icon className={`w-5 h-5 mr-3 ${active ? 'text-primary-600' : 'text-surface-400'}`} />
        {item.name}
        {badge && (
          <span className={`ml-auto ${badge.color} text-white text-xs font-medium rounded-full px-1.5 py-0.5 min-w-[20px] text-center leading-none`}>
            {badge.count}
          </span>
        )}
      </NavLink>
    );
  };

  const renderSection = (section: NavSection, key: string) => {
    const visibleItems = section.items.filter(canSee);
    if (visibleItems.length === 0) return null;
    return (
      <div key={key} className="pt-3">
        {section.label && (
          <p className="px-3 pb-1 text-[11px] font-semibold text-surface-400 uppercase tracking-wider">
            {section.label}
          </p>
        )}
        <div className="space-y-0.5">
          {visibleItems.map((item) => renderNavItem(item, visibleItems))}
        </div>
      </div>
    );
  };

  const renderAdvancedDrawer = (items: NavItem[]) => {
    const visibleItems = items.filter(canSee);
    if (visibleItems.length === 0) return null;
    return (
      <div className="pt-3 mt-3 border-t border-surface-200">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="flex items-center w-full !justify-start !px-3 !py-2 !text-[11px] !font-semibold !text-surface-400 !uppercase tracking-wider hover:!text-surface-600"
          aria-expanded={advancedOpen}
          aria-controls="sidebar-advanced-drawer"
        >
          {advancedOpen ? (
            <ChevronDown className="w-3.5 h-3.5 mr-1.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 mr-1.5" />
          )}
          Advanced
        </Button>
        {advancedOpen && (
          <div id="sidebar-advanced-drawer" className="space-y-0.5 mt-1">
            {visibleItems.map((item) => renderNavItem(item, visibleItems))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white border-r border-surface-300 w-64">
      {/* Logo */}
      <div className="flex items-center justify-between h-16 border-b border-surface-200 px-5">
        <img src={logoFull} alt="LasoXP" className="h-7" />
        {mobile && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={onClose}
            aria-label="Close menu"
            className="p-1 rounded-md text-surface-500 hover:text-surface-700 hover:bg-surface-100"
          >
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {showAdminMode && (
          <>
            {/* ── Super Admin view ── */}
            {adminMainSections.map((section, idx) => renderSection(section, `admin-${idx}`))}
            {renderAdvancedDrawer(adminAdvancedItems)}
          </>
        )}

        {isPropertyMode && (
          <>
            {/* Back to Admin for super admins */}
            {isSuperAdmin && (
              <Button
                type="button"
                variant="ghost"
                fullWidth
                onClick={() => {
                  setSelectedProperty(null, false);
                  handleNavClick();
                  navigate('/');
                }}
                className="!justify-start px-3 py-2 mb-3 text-sm !text-surface-500 hover:!text-surface-700"
              >
                <ArrowLeft className="w-4 h-4 mr-3" />
                Back to Admin
              </Button>
            )}

            {/* Property name header */}
            <div className="px-3 pb-2 mb-1 border-b border-surface-200">
              <p className="text-xs font-semibold text-surface-400 uppercase tracking-wider">Property</p>
              <p className="text-sm font-medium text-surface-800 truncate">{selectedProperty.name}</p>
            </div>

            {/* Four main product-job sections */}
            {propertySections.map((section, idx) => renderSection(section, `prop-${idx}`))}

            {/* Account group — visually separated from main sections */}
            {propertyAccountSection && (
              <div className="pt-3 mt-3 border-t border-surface-200">
                {(() => {
                  const visible = propertyAccountSection.items.filter(canSee);
                  if (visible.length === 0) return null;
                  return (
                    <>
                      {propertyAccountSection.label && (
                        <p className="px-3 pb-1 text-[11px] font-semibold text-surface-400 uppercase tracking-wider">
                          {propertyAccountSection.label}
                        </p>
                      )}
                      <div className="space-y-0.5">
                        {visible.map((item) => renderNavItem(item, visible))}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Advanced drawer — collapsible, role-gated items only */}
            {renderAdvancedDrawer(propertyAdvancedItems)}
          </>
        )}
      </nav>

      {/* User Profile & Sign Out */}
      <div className="border-t border-surface-200 p-4">
        <div className="flex items-center mb-3">
          <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
            {adminUser?.displayName?.[0] || adminUser?.email?.[0]?.toUpperCase()}
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-surface-800 truncate">
              {adminUser?.displayName || 'Admin User'}
            </p>
            <p className="text-xs text-surface-500 truncate">{adminUser?.email}</p>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          fullWidth
          onClick={handleSignOut}
          className="!justify-start px-3 py-2 text-sm !text-surface-500 hover:!text-surface-700"
        >
          <LogOut className="w-4 h-4 mr-3" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}
